import { useState, useEffect } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { PMDialog } from "../../utils/PMDialog";
import { PMField } from "../../utils/PMField";
import { PMButton } from "../../utils/PMButton";
import { PMNotice } from "../../utils/notifications";
import {
  format,
  parseISO,
  eachDayOfInterval,
  isSameDay,
  startOfDay,
  isBefore,
  addDays,
  addMinutes,
  differenceInMinutes,
} from "date-fns";
import { nb } from "date-fns/locale";
import { fetchWorkOrder } from "../../../api/work-order";
import { fetchUserWorkSchedule } from "../../../api/user";
import { createEvent, updateEvent, deleteEvent } from "../../../api/events";
import { fetchUsers } from "../../../api/users";
import { stripHtml } from "../../Utils";

function mapWorkOrderRoleToEventRole(woRole) {
  if (woRole === "RESPONSIBLE") return "RESPONSIBLE";
  if (woRole === "OBSERVER") return "OBSERVER";
  if (woRole === "EXECUTOR") return "PARTICIPANT";
  return "PARTICIPANT";
}

function buildAttendeeRowsFromWorkOrder(wo) {
  const seen = new Set();
  const rows = [];
  for (const p of wo?.participants || []) {
    const uid = p.user_id || p.user?.id;
    if (!uid || seen.has(uid)) continue;
    seen.add(uid);
    const woRole = p.role || "";
    rows.push({
      key: `wo-${uid}`,
      user_id: uid,
      name: p.user?.displayTitle || p.user?.name || "—",
      work_order_role: woRole,
      event_role: mapWorkOrderRoleToEventRole(woRole),
      included: true,
      from_wo: true,
      status: "ASSIGNED",
      notes: "",
    });
  }
  return rows;
}

function buildAttendeeRowsFromEvent(ev) {
  return (ev?.participants || []).map((p, idx) => ({
    key: `ev-${p.id || p.user_id || idx}`,
    user_id: p.user_id,
    name: p.user_name || p.user?.displayTitle || "—",
    work_order_role: "",
    event_role: p.role || "PARTICIPANT",
    included: true,
    from_wo: false,
    status: p.status || "ASSIGNED",
    notes: p.notes || "",
  }));
}

/** Same priority as WorkOrder#event_user_id (API): executor roles, then other field staff — not RESPONSIBLE alone. */
function getWorkOrderEventOwnerUser(wo) {
  if (!wo) return null;
  const exec =
    wo.responsible_executor_participant?.user ||
    (wo.participants || []).find((p) => p.role === "RESPONSIBLE_EXECUTOR")?.user;
  if (exec) return exec;
  const executor = (wo.participants || []).find((p) => p.role === "EXECUTOR")?.user;
  if (executor) return executor;
  const field = (wo.participants || []).find(
    (p) => p.role !== "RESPONSIBLE" && p.role !== "OBSERVER"
  )?.user;
  if (field) return field;
  return wo.created_by || null;
}

const DEFAULT_EVENT_DURATION_MINUTES = 120;
const EVENT_DURATION_PRESETS = [60, 120, 240, 480];

function getDefaultEventTitleFromWorkOrder(wo) {
  return (
    stripHtml(wo?.displayTitle || "") ||
    stripHtml(wo?.title || "") ||
    (wo?.id ? `Arbeidsordre #${wo.id}` : "Arbeidsordre")
  );
}

function getDefaultEventDescriptionFromWorkOrder(wo) {
  return stripHtml(wo?.description || "");
}

function toDateTimeLocalValue(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return format(new Date(), "yyyy-MM-dd'T'HH:mm");
  }
  return format(date, "yyyy-MM-dd'T'HH:mm");
}

function roundUpToNearestHalfHour(value) {
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return new Date();
  }

  date.setSeconds(0, 0);
  const minutes = date.getMinutes();
  const remainder = minutes % 30;

  if (remainder !== 0) {
    date.setMinutes(minutes + (30 - remainder));
  }

  return date;
}

function normalizeDurationMinutes(value) {
  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return DEFAULT_EVENT_DURATION_MINUTES;
  }
  return Math.max(30, Math.round(minutes / 15) * 15);
}

function calculateDurationMinutes(startValue, endValue) {
  const start = new Date(startValue);
  const end = new Date(endValue);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return DEFAULT_EVENT_DURATION_MINUTES;
  }

  return normalizeDurationMinutes(differenceInMinutes(end, start));
}

function calculateEndTimeFromDuration(startValue, durationMinutes) {
  const start = new Date(startValue);
  if (Number.isNaN(start.getTime())) {
    return toDateTimeLocalValue(new Date());
  }

  return toDateTimeLocalValue(
    addMinutes(start, normalizeDurationMinutes(durationMinutes))
  );
}

function formatEventDateTimeSummary(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("nb-NO", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

export const WorkOrderEventDialog = ({
  open,
  onClose,
  workOrder,
  event = null,
  onEventsMutated,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState([]);
  const [currentWorkOrder, setCurrentWorkOrder] = useState(workOrder);
  const [isLoadingWorkOrder, setIsLoadingWorkOrder] = useState(false);
  const [userWorkSchedule, setUserWorkSchedule] = useState(null);
  const [isLoadingWorkSchedule, setIsLoadingWorkSchedule] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [responsibleExecutor, setResponsibleExecutor] = useState(null);
  const [scheduleError, setScheduleError] = useState(null);
  const [eventAttendeeRows, setEventAttendeeRows] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [showAddAttendee, setShowAddAttendee] = useState(false);
  const [addAttendeeUserId, setAddAttendeeUserId] = useState("");

  const isEditing = !!event;

  // Fetch available users for participant selection
  const fetchAvailableUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const controller = {
        filters: [{ name: "employee", value: true }],
        page: 1,
        query: "",
        sort: "desc",
        sortBy: "id",
        view: "all",
      };

      const response = await fetchUsers(controller);
      const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : [];
      setAvailableUsers(
        list.map((user) => ({
          id: user.id,
          name: user.displayTitle,
          displayTitle: user.displayTitle,
          email: user.email,
        }))
      );
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Refresh work order data when dialog opens
  useEffect(() => {
    if (open) {
      // Load available users
      fetchAvailableUsers();

      if (workOrder?.id) {
        setIsLoadingWorkOrder(true);
        // Fetch latest work order data using the API function
        fetchWorkOrder(workOrder.id)
          .then((response) => {
            const data = response.data;
            setCurrentWorkOrder(data);
            const owner = getWorkOrderEventOwnerUser(data);
            setResponsibleExecutor(owner ? { user: owner } : null);
          })
          .catch((error) => {
            console.error("Error fetching work order:", error);
            // Fallback to original work order if fetch fails
            setCurrentWorkOrder(workOrder);
            const owner = getWorkOrderEventOwnerUser(workOrder);
            setResponsibleExecutor(owner ? { user: owner } : null);
          })
          .finally(() => {
            setIsLoadingWorkOrder(false);
          });
      }
    }
  }, [open, workOrder?.id]);

  useEffect(() => {
    if (!open || isEditing || !currentWorkOrder?.id) return;
    setEventAttendeeRows(buildAttendeeRowsFromWorkOrder(currentWorkOrder));
  }, [
    open,
    isEditing,
    currentWorkOrder?.id,
    (currentWorkOrder?.participants || []).length,
  ]);

  useEffect(() => {
    if (!open || !isEditing || !event) return;
    setEventAttendeeRows(buildAttendeeRowsFromEvent(event));
  }, [open, isEditing, event?.id, event?.participants]);

  // Fetch user's work schedule when dialog opens
  useEffect(() => {
    if (open && responsibleExecutor?.user?.id) {
      const fetchWorkSchedule = async () => {
        try {
          setLoadingSchedule(true);

          const response = await fetchUserWorkSchedule(
            responsibleExecutor.user.id
          );

          if (response && !response.error) {
            setUserWorkSchedule(response);
            setScheduleError(null);

          } else {

            setUserWorkSchedule(null);
            setScheduleError(
              response?.message || "Kunne ikke hente arbeidstidsplan"
            );
          }
        } catch (error) {
          console.error("Error fetching work schedule:", error);
          PMNotice.error("Kunne ikke hente arbeidstidsplan");
        } finally {
          setLoadingSchedule(false);
        }
      };

      fetchWorkSchedule();
    }
  }, [open, responsibleExecutor?.user?.id]);

  // Helper function to get working hours for a specific date from work schedule
  const getWorkingHoursForDate = (date, schedule) => {
    // If no schedule available, return default times
    if (!schedule || !date) {
      return { from: "08:00", to: "16:00", off: false };
    }



    // Convert Norwegian day names to English for the schedule lookup
    const dayNameMap = {
      "mandag": "monday",
      "tirsdag": "tuesday",
      "onsdag": "wednesday",
      "torsdag": "thursday",
      "fredag": "friday",
      "lørdag": "saturday",
      "søndag": "sunday",
    };

    const norwegianDay = format(date, "EEEE", { locale: nb }).toLowerCase();
    const englishDay = dayNameMap[norwegianDay];



    // Try scaled_weekdays first (if employee data is available), then fallback to weekdays
    const weekdays = schedule.scaled_weekdays || schedule.weekdays || {};

    const dayHours = weekdays[englishDay] || weekdays[norwegianDay] || 0;

    if (!dayHours || dayHours === 0) {
      return { from: "08:00", to: "16:00", off: true };
    }

    // Convert decimal hours to proper time format
    const startHour = 8; // Default start time
    const totalMinutes = Math.round(dayHours * 60); // Convert hours to minutes
    const endHour = Math.floor(totalMinutes / 60) + startHour;
    const endMinute = totalMinutes % 60;

    const result = {
      from: `${startHour.toString().padStart(2, "0")}:00`,
      to: `${endHour.toString().padStart(2, "0")}:${endMinute
        .toString()
        .padStart(2, "0")}`,
      off: false,
    };


    return result;
  };

  // Generate working hours for a date range based on work schedule
  const generateWorkingHoursForRange = (startDate, endDate, schedule) => {
    if (!startDate || !endDate) return {};

    const start = parseISO(startDate);
    const end = parseISO(endDate);
    const workingHours = {};

    // Generate for each day in the range
    let currentDate = startOfDay(start);
    while (isBefore(currentDate, end) || isSameDay(currentDate, end)) {
      const dateKey = format(currentDate, "yyyy-MM-dd");
      const dayHours = getWorkingHoursForDate(currentDate, schedule);

      workingHours[dateKey] = {
        from: dayHours.from,
        to: dayHours.to,
        off: dayHours.off,
      };

      currentDate = addDays(currentDate, 1);
    }

    return workingHours;
  };

  // Working Hours Section Component
  const WorkingHoursSection = ({ formik }) => {
    const startDate = formik.values.start_time;
    const endDate = formik.values.end_time;

    if (!startDate || !endDate) return null;

    const start = parseISO(startDate);
    const end = parseISO(endDate);

    // Only show if event spans multiple days
    if (isSameDay(start, end)) return null;

    const days = eachDayOfInterval({ start, end });

    return (
      <div className="space-y-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-3">Arbeidstid per dag</h4>
          <p className="text-sm text-gray-500 mb-4">
            Rediger arbeidstid for hver dag eller sett dager som fri
          </p>
        </div>

        {loadingSchedule && (
          <div className="text-sm text-gray-500">
            Henter arbeidstid fra ansvarlig utførende...
          </div>
        )}

        {scheduleError && (
          <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-md mb-4">
            <div className="font-medium">Bruker standard tider</div>
            <div>{scheduleError}</div>
          </div>
        )}

        <div className="space-y-3">
          {days.map((day, index) => {
            const dateKey = format(day, "yyyy-MM-dd");
            const dayName = format(day, "EEEE", { locale: nb });
            const dayDate = format(day, "d. MMMM", { locale: nb });
            const workingHours = formik.values.working_hours[dateKey] || {
              from: "08:00",
              to: "16:00",
              off: false,
            };

            return (
              <div
                key={dateKey}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h5 className="font-medium text-gray-900">{dayName}</h5>
                    <p className="text-sm text-gray-500">{dayDate}</p>
                  </div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={workingHours.off}
                      onChange={(e) => {
                        const newWorkingHours = {
                          ...formik.values.working_hours,
                        };
                        newWorkingHours[dateKey] = {
                          ...workingHours,
                          off: e.target.checked,
                        };
                        formik.setFieldValue("working_hours", newWorkingHours);
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Fri dag</span>
                  </label>
                </div>

                {!workingHours.off && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Starttid
                      </label>
                      <input
                        type="time"
                        value={workingHours.from}
                        onChange={(e) => {
                          const newWorkingHours = {
                            ...formik.values.working_hours,
                          };
                          newWorkingHours[dateKey] = {
                            ...workingHours,
                            from: e.target.value,
                          };
                          formik.setFieldValue(
                            "working_hours",
                            newWorkingHours
                          );
                        }}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sluttid
                      </label>
                      <input
                        type="time"
                        value={workingHours.to}
                        onChange={(e) => {
                          const newWorkingHours = {
                            ...formik.values.working_hours,
                          };
                          newWorkingHours[dateKey] = {
                            ...workingHours,
                            to: e.target.value,
                          };
                          formik.setFieldValue(
                            "working_hours",
                            newWorkingHours
                          );
                        }}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Helper function to build delivery address string
  const buildDeliveryAddress = (deliveryAddress) => {
    if (!deliveryAddress) return "";

    const addressParts = [];
    if (deliveryAddress.name) addressParts.push(deliveryAddress.name);
    if (deliveryAddress.address) addressParts.push(deliveryAddress.address);
    if (deliveryAddress.postal) addressParts.push(deliveryAddress.postal);
    if (deliveryAddress.place) addressParts.push(deliveryAddress.place);

    return addressParts.join(", ");
  };

  // Helper function to convert backend working_hours format to frontend format
  const convertWorkingHoursFromBackend = (backendWorkingHours) => {
    if (!backendWorkingHours) return {};

    // Single-day timed events: no per-day map in the form
    if (backendWorkingHours.type === "specific_time") {
      return {};
    }

    // If it's already in frontend format (object with date keys), return as is
    if (
      typeof backendWorkingHours === "object" &&
      !Array.isArray(backendWorkingHours) &&
      backendWorkingHours.schedule === undefined
    ) {
      return backendWorkingHours;
    }

    // If it's the new JSONB format with schedule array
    if (
      backendWorkingHours.schedule &&
      Array.isArray(backendWorkingHours.schedule)
    ) {
      const frontendFormat = {};
      backendWorkingHours.schedule.forEach((day) => {
        frontendFormat[day.date] = {
          from: day.start_time || "08:00",
          to: day.end_time || "16:00",
          off: day.day_off || false,
        };
      });
      return frontendFormat;
    }

    // If it's a numeric value (old format), return empty object
    if (typeof backendWorkingHours === "number") {
      return {};
    }

    return {};
  };

  // Helper function to convert frontend working_hours format to backend format
  const convertWorkingHoursToBackend = (frontendWorkingHours) => {
    if (
      !frontendWorkingHours ||
      Object.keys(frontendWorkingHours).length === 0
    ) {
      return null;
    }

    // Backend `specific_time` blob — never map keys like "type" into a fake schedule
    if (frontendWorkingHours.type === "specific_time") {
      return null;
    }

    const schedule = Object.keys(frontendWorkingHours).map((date) => {
      const day = frontendWorkingHours[date];

      // Validate date format
      let weekday = "unknown";
      try {
        const dateObj = new Date(date);
        if (!isNaN(dateObj.getTime())) {
          weekday = format(dateObj, "EEEE", { locale: nb }).toLowerCase();
        }
      } catch (error) {
        console.warn("Invalid date format:", date);
      }

      return {
        date: date,
        weekday: weekday,
        hours: day.off ? 0 : calculateHoursFromTimes(day.from, day.to),
        day_off: day.off || false,
        start_time: day.off ? null : day.from || null,
        end_time: day.off ? null : day.to || null,
      };
    });

    const totalHours = schedule.reduce((sum, day) => sum + day.hours, 0);

    return {
      total_hours: totalHours,
      schedule: schedule,
      type: "all_day",
    };
  };

  // Helper function to calculate hours from start and end times
  const calculateHoursFromTimes = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;

    // Validate time format (HH:MM)
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(startTime) || !timeRegex.test(endTime)) {
      console.warn("Invalid time format:", { startTime, endTime });
      return 0;
    }

    try {
      const start = new Date(`2000-01-01T${startTime}:00`);
      const end = new Date(`2000-01-01T${endTime}:00`);

      // Check if dates are valid
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        console.warn("Invalid date created from times:", {
          startTime,
          endTime,
        });
        return 0;
      }

      const diffMs = end - start;
      const diffHours = diffMs / (1000 * 60 * 60);

      return Math.max(0, diffHours);
    } catch (error) {
      console.error("Error calculating hours from times:", error, {
        startTime,
        endTime,
      });
      return 0;
    }
  };

  const workOrderRoleLabel = (role) => {
    if (role === "RESPONSIBLE") return t("RESPONSIBLE");
    if (role === "RESPONSIBLE_EXECUTOR") return t("Executor");
    if (role === "OBSERVER") return t("OBSERVER");
    if (role === "PARTICIPANT") return t("work_order.event.role_participant");
    return role || "";
  };

  const toggleAttendeeIncluded = (key) => {
    setEventAttendeeRows((rows) =>
      rows.map((r) =>
        r.key === key ? { ...r, included: !r.included } : r
      )
    );
  };

  const removeAttendeeRow = (key) => {
    setEventAttendeeRows((rows) => rows.filter((r) => r.key !== key));
  };

  const addExtraAttendee = () => {
    const uid = parseInt(addAttendeeUserId, 10);
    if (!uid) return;
    const user = availableUsers.find((u) => u.id === uid);
    if (!user) return;
    setEventAttendeeRows((rows) => {
      if (rows.some((r) => r.user_id === uid)) {
        return rows;
      }
      return [
        ...rows,
        {
          key: `extra-${uid}-${Date.now()}`,
          user_id: uid,
          name: user.displayTitle || user.name,
          work_order_role: "",
          event_role: "PARTICIPANT",
          included: true,
          from_wo: false,
          status: "ASSIGNED",
          notes: "",
        },
      ];
    });
    setAddAttendeeUserId("");
    setShowAddAttendee(false);
  };

  const attendeeUserIds = eventAttendeeRows.map((r) => r.user_id);
  const usersAvailableToAdd = availableUsers.filter(
    (u) => !attendeeUserIds.includes(u.id)
  );

  const defaultTitleFromWo = getDefaultEventTitleFromWorkOrder(currentWorkOrder);
  const defaultDescriptionFromWo =
    getDefaultEventDescriptionFromWorkOrder(currentWorkOrder);
  const deliveryAddressFromWo = buildDeliveryAddress(
    currentWorkOrder?.delivery_address
  );
  const initialStartTime = event?.start_time
    ? toDateTimeLocalValue(event.start_time)
    : toDateTimeLocalValue(roundUpToNearestHalfHour(new Date()));
  const initialEndTime = event?.end_time
    ? toDateTimeLocalValue(event.end_time)
    : calculateEndTimeFromDuration(
        initialStartTime,
        DEFAULT_EVENT_DURATION_MINUTES
      );
  const initialDurationMinutes = calculateDurationMinutes(
    initialStartTime,
    initialEndTime
  );
  const initialUseManualEnd =
    !!event?.all_day ||
    !isSameDay(new Date(initialStartTime), new Date(initialEndTime));

  const initialValues = {
    start_time: initialStartTime,
    end_time: initialEndTime,
    all_day: event?.all_day || false,
    project_id: event?.project_id || currentWorkOrder?.project_id,
    working_hours: convertWorkingHoursFromBackend(event?.working_hours) || {},
    duration_minutes: initialDurationMinutes,
    use_manual_end: initialUseManualEnd,
  };

  const validationSchema = Yup.object({
    start_time: Yup.date().required("Starttid er påkrevd"),
    end_time: Yup.date()
      .required("Sluttid er påkrevd")
      .min(Yup.ref("start_time"), "Sluttid må være etter starttid"),
  });

  const handleSubmit = async (values, helpers) => {
    try {
      helpers.setSubmitting(true);

      const eventOwnerUser = getWorkOrderEventOwnerUser(currentWorkOrder);
      const userIdForEvent =
        eventOwnerUser?.id ??
        (isEditing ? event?.user_id ?? event?.user?.id : null);
      if (!userIdForEvent) {
        setErrors([t("work_order.event.error_need_event_owner")]);
        helpers.setSubmitting(false);
        return;
      }

      const participantsPayload = eventAttendeeRows
        .filter((r) => r.included)
        .map((r) => ({
          user_id: r.user_id,
          role: r.event_role || "PARTICIPANT",
          status: r.status || "ASSIGNED",
          notes: r.notes || "",
        }));

      const eventData = {
        title: getDefaultEventTitleFromWorkOrder(currentWorkOrder),
        description: getDefaultEventDescriptionFromWorkOrder(currentWorkOrder),
        start_time: values.start_time,
        end_time: values.end_time,
        all_day: values.all_day,
        location: buildDeliveryAddress(currentWorkOrder?.delivery_address),
        project_id: values.project_id,
        module: "work_order",
        module_id: currentWorkOrder?.id,
        user_id: userIdForEvent,
        company_id:
          currentWorkOrder?.company?.id || currentWorkOrder?.company_id,
        participants: participantsPayload,
      };

      if (isEditing && event?.id) {
        eventData.id = event.id;
      }

      // All-day: send per-day plan. Timed events: omit — API derives `specific_time` from start/end.
      if (
        values.all_day &&
        values.working_hours &&
        Object.keys(values.working_hours).length > 0
      ) {
        const convertedWorkingHours = convertWorkingHoursToBackend(
          values.working_hours
        );
        if (convertedWorkingHours) {
          eventData.working_hours = convertedWorkingHours;
        }
      }

      let res;
      if (isEditing) {
        res = await updateEvent(eventData);
      } else {
        res = await createEvent(eventData);
      }

      const saved = res?.data ?? res;

      helpers.setSubmitting(false);
      onClose();
      if (onEventsMutated) {
        onEventsMutated({
          action: isEditing ? "updated" : "created",
          event: saved,
        });
      } else if (onRefresh) {
        onRefresh();
      }
      toast.success(
        isEditing
          ? t("work_order.event.toast_updated")
          : t("work_order.event.toast_created")
      );
    } catch (error) {
      helpers.setSubmitting(false);
      console.error("Error saving event:", error);

      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors(["Kunne ikke lagre hendelse"]);
      }

      helpers.setErrors({ submit: "Kunne ikke lagre hendelse" });
    }
  };

  const handleDelete = async () => {
    if (!event) return;

    try {
      await deleteEvent(event.id);
      onClose();
      if (onEventsMutated) {
        onEventsMutated({ action: "deleted", id: event.id });
      } else if (onRefresh) {
        onRefresh();
      }
      toast.success(t("work_order.event.toast_deleted"));
    } catch (error) {
      console.error("Error deleting event:", error);

      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors([t("work_order.event.delete_failed")]);
      }

      toast.error(t("work_order.event.delete_failed"));
    }
  };

  return (
    <PMDialog
      title={
        isEditing
          ? t("work_order.event.dialog_edit")
          : t("work_order.event.dialog_create")
      }
      open={open}
      onClose={onClose}
      size="2xl"
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formik) => {
          return (
            <Form>
              {isLoadingWorkOrder && (
                <div className="mb-4">
                  <PMNotice
                    severity="info"
                    title="Henter oppdatert informasjon"
                    description="Henter siste endringer fra arbeidsordren..."
                  />
                </div>
              )}

              {errors && errors.length > 0 && (
                <div className="mb-4">
                  {errors.map((error, index) => (
                    <PMNotice
                      key={index}
                      severity="error"
                      title="Feil!"
                      description={error}
                    />
                  ))}
                </div>
              )}

              {!getWorkOrderEventOwnerUser(currentWorkOrder) &&
                !(
                  isEditing &&
                  (event?.user_id != null || event?.user?.id != null)
                ) && (
                <div className="mb-4">
                  <PMNotice
                    severity="warning"
                    title={t("work_order.event.notice_no_event_owner_title")}
                    description={t(
                      "work_order.event.notice_no_event_owner_description"
                    )}
                  />
                </div>
              )}

              <div className="space-y-6">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                  <div>
                    <h4 className="font-medium text-gray-900">
                      Hentet fra arbeidsordre
                    </h4>
                    <p className="text-sm text-gray-500">
                      Tittel, beskrivelse og leveringsadresse styres fra
                      arbeidsordren.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div className="min-w-0">
                      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        Tittel
                      </div>
                      <div className="mt-1 text-sm text-gray-900 break-words">
                        {defaultTitleFromWo}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        Leveringsadresse
                      </div>
                      <div className="mt-1 text-sm text-gray-900 break-words">
                        {deliveryAddressFromWo ||
                          "Leveringsadresse mangler på arbeidsordren"}
                      </div>
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Beskrivelse
                    </div>
                    <div className="mt-1 text-sm text-gray-900 whitespace-pre-wrap break-words">
                      {defaultDescriptionFromWo ||
                        "Ingen beskrivelse på arbeidsordren"}
                    </div>
                  </div>
                </div>

                {!deliveryAddressFromWo && (
                  <PMNotice
                    severity="warning"
                    title="Leveringsadresse mangler"
                    description="Lokasjon hentes nå fra arbeidsordren. Legg inn leveringsadresse på arbeidsordren hvis hendelsen trenger adresse."
                  />
                )}

                {/* Date and Time */}
                <div className="space-y-4 rounded-lg border border-gray-200 p-4">
                  <div>
                    <h4 className="font-medium text-gray-900">Tidspunkt</h4>
                    <p className="text-sm text-gray-500">
                      Starttid er hovedvalget. Varighet brukes som standard for
                      korte hendelser, og sluttid kan overstyres ved behov.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <PMField
                        name="start_time"
                        type="datetime-local"
                        label={t("work_order.event.field_start")}
                        {...formik}
                        onChange={(e) => {
                          const nextStartTime = e.target.value;
                          formik.setFieldValue("start_time", nextStartTime);

                          if (
                            !formik.values.use_manual_end &&
                            !formik.values.all_day
                          ) {
                            formik.setFieldValue(
                              "end_time",
                              calculateEndTimeFromDuration(
                                nextStartTime,
                                formik.values.duration_minutes
                              )
                            );
                          }
                        }}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="duration_hours"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Varighet (timer)
                      </label>
                      <div className="mt-2 space-y-3">
                        <div className="flex flex-wrap gap-2">
                          {EVENT_DURATION_PRESETS.map((minutes) => (
                            <button
                              key={minutes}
                              type="button"
                              disabled={formik.values.all_day}
                              onClick={() => {
                                formik.setFieldValue(
                                  "duration_minutes",
                                  minutes
                                );
                                formik.setFieldValue("use_manual_end", false);
                                formik.setFieldValue(
                                  "end_time",
                                  calculateEndTimeFromDuration(
                                    formik.values.start_time,
                                    minutes
                                  )
                                );
                              }}
                              className={`rounded-md border px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 ${
                                Number(formik.values.duration_minutes) ===
                                  minutes && !formik.values.use_manual_end
                                  ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                                  : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                              }`}
                            >
                              {minutes / 60} t
                            </button>
                          ))}
                        </div>
                        <input
                          id="duration_hours"
                          name="duration_hours"
                          type="number"
                          min="0.5"
                          step="0.5"
                          disabled={formik.values.all_day}
                          value={(
                            normalizeDurationMinutes(
                              formik.values.duration_minutes
                            ) / 60
                          ).toString()}
                          onChange={(e) => {
                            const nextHours = Number(e.target.value);
                            const nextMinutes = normalizeDurationMinutes(
                              nextHours * 60
                            );
                            formik.setFieldValue(
                              "duration_minutes",
                              nextMinutes
                            );
                            formik.setFieldValue("use_manual_end", false);
                            formik.setFieldValue(
                              "end_time",
                              calculateEndTimeFromDuration(
                                formik.values.start_time,
                                nextMinutes
                              )
                            );
                          }}
                          className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm disabled:cursor-not-allowed disabled:bg-gray-100"
                        />
                        {!formik.values.use_manual_end && (
                          <p className="text-sm text-gray-500">
                            Slutter{" "}
                            {formatEventDateTimeSummary(formik.values.end_time)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formik.values.use_manual_end}
                        disabled={formik.values.all_day}
                        onChange={(e) => {
                          const useManualEnd = e.target.checked;
                          formik.setFieldValue("use_manual_end", useManualEnd);

                          if (!useManualEnd) {
                            formik.setFieldValue(
                              "end_time",
                              calculateEndTimeFromDuration(
                                formik.values.start_time,
                                formik.values.duration_minutes
                              )
                            );
                          } else {
                            formik.setFieldValue(
                              "duration_minutes",
                              calculateDurationMinutes(
                                formik.values.start_time,
                                formik.values.end_time
                              )
                            );
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Sett slutt manuelt
                      </span>
                    </label>
                    {formik.values.use_manual_end && (
                      <span className="text-sm text-gray-500">
                        Bruk dette for flerdagers hendelser eller avvikende
                        sluttid.
                      </span>
                    )}
                  </div>
                  {formik.values.use_manual_end && (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <PMField
                          name="end_time"
                          type="datetime-local"
                          label={t("work_order.event.field_end")}
                          {...formik}
                          onChange={(e) => {
                            const nextEndTime = e.target.value;
                            formik.setFieldValue("end_time", nextEndTime);
                            formik.setFieldValue(
                              "duration_minutes",
                              calculateDurationMinutes(
                                formik.values.start_time,
                                nextEndTime
                              )
                            );
                          }}
                        />
                      </div>
                      <div className="rounded-md bg-gray-50 px-4 py-3 text-sm text-gray-600">
                        Varighet beregnet til{" "}
                        <span className="font-medium text-gray-900">
                          {(
                            normalizeDurationMinutes(
                              formik.values.duration_minutes
                            ) / 60
                          )
                            .toString()
                            .replace(".0", "")}{" "}
                          timer
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* All Day Toggle */}
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formik.values.all_day}
                      onChange={(e) => {
                        formik.setFieldValue("all_day", e.target.checked);
                        if (e.target.checked) {
                          formik.setFieldValue("use_manual_end", true);
                          // If all day, set times to start and end of day
                          const startDate = new Date(formik.values.start_time);
                          const endDate = new Date(formik.values.end_time);
                          startDate.setHours(0, 0, 0, 0);
                          endDate.setHours(23, 59, 59, 999);
                          formik.setFieldValue(
                            "start_time",
                            format(startDate, "yyyy-MM-dd'T'HH:mm")
                          );
                          formik.setFieldValue(
                            "end_time",
                            format(endDate, "yyyy-MM-dd'T'HH:mm")
                          );
                          formik.setFieldValue(
                            "duration_minutes",
                            calculateDurationMinutes(startDate, endDate)
                          );
                        } else {
                          formik.setFieldValue("use_manual_end", false);
                          formik.setFieldValue("working_hours", {});
                          formik.setFieldValue(
                            "end_time",
                            calculateEndTimeFromDuration(
                              formik.values.start_time,
                              formik.values.duration_minutes
                            )
                          );
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      {t("work_order.event.all_day")}
                    </span>
                  </label>
                </div>

                {/* Event attendees (work order participants pre-selected; uncheck to exclude) */}
                <div className="space-y-3 border-t border-gray-100 pt-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h4 className="font-medium text-gray-900">
                      {t("work_order.event.attendees_title")}
                    </h4>
                    <button
                      type="button"
                      onClick={() => setShowAddAttendee(true)}
                      className="text-sm bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700"
                    >
                      {t("work_order.event.add_attendee")}
                    </button>
                  </div>
                  <p className="text-sm text-gray-500">
                    {t("work_order.event.attendees_hint")}
                  </p>
                  {eventAttendeeRows.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      {t("work_order.event.no_attendees_from_wo")}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {eventAttendeeRows.map((row) => (
                        <div
                          key={row.key}
                          className="flex items-center justify-between gap-3 p-3 border border-gray-200 rounded-md"
                        >
                          <label className="flex items-center gap-3 flex-1 cursor-pointer min-w-0">
                            <input
                              type="checkbox"
                              checked={row.included}
                              onChange={() => toggleAttendeeIncluded(row.key)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 shrink-0"
                            />
                            <div className="min-w-0">
                              <div className="font-medium text-gray-900 truncate">
                                {row.name}
                              </div>
                              {row.from_wo && row.work_order_role ? (
                                <div className="text-xs text-gray-500">
                                  {workOrderRoleLabel(row.work_order_role)}
                                </div>
                              ) : null}
                            </div>
                          </label>
                          <div className="shrink-0 flex items-center gap-2">
                            {row.from_wo ? (
                              <span className="text-xs text-gray-400">
                                {t("work_order.event.from_work_order")}
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => removeAttendeeRow(row.key)}
                                className="text-sm text-red-600 hover:text-red-800"
                              >
                                {t("work_order.event.remove_attendee")}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {showAddAttendee && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
                      <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4 shadow-lg">
                        <h3 className="text-lg font-medium mb-4">
                          {t("work_order.event.add_attendee")}
                        </h3>
                        {isLoadingUsers ? (
                          <div className="text-center py-4 text-gray-500">
                            {t("work_order.event.loading_users")}
                          </div>
                        ) : (
                          <>
                            <select
                              value={addAttendeeUserId}
                              onChange={(e) =>
                                setAddAttendeeUserId(e.target.value)
                              }
                              className="w-full p-2 border border-gray-300 rounded-md mb-4"
                            >
                              <option value="">
                                {t("work_order.event.select_user")}
                              </option>
                              {usersAvailableToAdd.map((user) => (
                                <option key={user.id} value={user.id}>
                                  {user.displayTitle}
                                </option>
                              ))}
                            </select>
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setShowAddAttendee(false);
                                  setAddAttendeeUserId("");
                                }}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                              >
                                {t("Cancel")}
                              </button>
                              <button
                                type="button"
                                onClick={addExtraAttendee}
                                disabled={!addAttendeeUserId}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                              >
                                {t("work_order.event.add_confirm")}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Working Hours Section */}
                <WorkingHoursSection formik={formik} />

                {/* Action Buttons */}
                <div className="flex justify-between pt-4">
                  <div className="flex gap-2">
                    {isEditing && (
                      <PMButton
                        type="button"
                        onClick={handleDelete}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        {t("work_order.event.delete_event")}
                      </PMButton>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <PMButton
                      type="button"
                      onClick={onClose}
                      className="bg-gray-300 hover:bg-gray-400 text-gray-700"
                    >
                      {t("Cancel")}
                    </PMButton>
                    <PMButton
                      type="submit"
                      disabled={formik.isSubmitting || isLoadingWorkOrder}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {formik.isSubmitting
                        ? t("work_order.event.saving")
                        : isEditing
                        ? t("work_order.event.submit_update")
                        : t("work_order.event.submit_create")}
                    </PMButton>
                  </div>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </PMDialog>
  );
};
